import { useEffect, useMemo, useState } from 'react';
import { CONFIG } from '../config.js';
import { LIMITS, sanitizeCantidad } from '../../shared/validation.js';

export const PreviewPanel = ({
  productos,
  cantidades,
  nombre,
  empresa,
  enviando,
  onNombreChange,
  onEmpresaChange,
  onEnviar,
  onCantidadChange
}) => {
  const [editingId, setEditingId] = useState(null);
  const [borrador, setBorrador] = useState('');

  useEffect(() => {
    if (!CONFIG.turnstileSiteKey) return;
    if (document.querySelector('script[data-turnstile]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-turnstile', 'true');
    document.head.appendChild(s);
  }, []);

  const productosSeleccionados = useMemo(() => {
    return productos
      .filter(p => {
        const qty = parseInt(cantidades[p.id]);
        return !isNaN(qty) && qty > 0;
      })
      .map(p => ({
        ...p,
        cantidad: parseInt(cantidades[p.id])
      }));
  }, [productos, cantidades]);

  const iniciarEdicion = (id, cantidadActual) => {
    setEditingId(id);
    setBorrador(String(cantidadActual));
  };

  const confirmarEdicion = (id) => {
    const limpio = sanitizeCantidad(borrador);
    if (limpio !== null && limpio !== '' && parseInt(limpio) > 0) {
      onCantidadChange(id, limpio);
    }
    // Si el borrador es 0/vacío/inválido, no tocamos cantidades:
    // el producto mantiene su valor previo en lugar de eliminarse.
    setEditingId(null);
    setBorrador('');
  };

  const cancelarEdicion = () => {
    setEditingId(null);
    setBorrador('');
  };

  const isDisabled =
    productosSeleccionados.length === 0 ||
    nombre.trim() === '' ||
    empresa.trim() === '';

  return (
    <div className="preview-panel">
      <div className="preview-content">
        <div className="preview-header">
          <h2>Tu pedido</h2>
          {productosSeleccionados.length > 0 && (
            <span className="badge">
              {productosSeleccionados.length} {productosSeleccionados.length === 1 ? 'producto' : 'productos'}
            </span>
          )}
        </div>

        {productosSeleccionados.length === 0 ? (
          <div className="empty-preview">Todavía no agregaste productos</div>
        ) : (
          <>
            <ul className="preview-list">
              {productosSeleccionados.map(p => (
                <li key={p.id} className="preview-item">
                  <span className="preview-item-text">
                    {p.codigo}. {p.nombre}{p.unidad ? ` - ${p.unidad}` : ''}
                  </span>
                  <div className="preview-item-controls">
                    {editingId === p.id ? (
                      <input
                        type="number"
                        className="preview-qty-input"
                        value={borrador}
                        onChange={(e) => {
                          const limpio = sanitizeCantidad(e.target.value);
                          if (limpio !== null) setBorrador(limpio);
                        }}
                        onBlur={() => confirmarEdicion(p.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            confirmarEdicion(p.id);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelarEdicion();
                          }
                          if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        autoFocus
                        min="1"
                        max={LIMITS.CANTIDAD_MAX}
                        inputMode="numeric"
                        aria-label="Editar cantidad"
                      />
                    ) : (
                      <span
                        className="preview-qty-display clickable"
                        onClick={() => iniciarEdicion(p.id, p.cantidad)}
                        title="Click para editar cantidad (Enter para guardar, Esc para cancelar)"
                        role="button"
                        tabIndex="0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            iniciarEdicion(p.id, p.cantidad);
                          }
                        }}
                      >
                        {p.cantidad}
                      </span>
                    )}
                    <button
                      className="preview-delete-btn"
                      onClick={() => onCantidadChange(p.id, '0')}
                      aria-label="Eliminar producto"
                      type="button"
                    >×</button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="preview-total">
              Total: {productosSeleccionados.length} {productosSeleccionados.length === 1 ? 'producto' : 'productos'}
            </div>
          </>
        )}

        <div className="preview-divider" />

        <input
          type="text"
          className="form-input"
          placeholder="Tu nombre"
          maxLength={LIMITS.NOMBRE_MAX}
          autoComplete="name"
          value={nombre}
          onChange={(e) => onNombreChange(e.target.value)}
        />

        <input
          type="text"
          className="form-input"
          placeholder="Empresa"
          maxLength={LIMITS.EMPRESA_MAX}
          autoComplete="organization"
          value={empresa}
          onChange={(e) => onEmpresaChange(e.target.value)}
        />

        {CONFIG.turnstileSiteKey && (
          <div className="cf-turnstile" data-sitekey={CONFIG.turnstileSiteKey} />
        )}

        <button
          className="btn-whatsapp"
          onClick={onEnviar}
          disabled={isDisabled || enviando}
        >
          {enviando ? 'Enviando...' : 'Enviar por WhatsApp'}
        </button>
      </div>
    </div>
  );
};
